import { css } from '@emotion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cytoscape from 'cytoscape';
import cytoscapeDagre from 'cytoscape-dagre';
import style from './TaskGraph.cytoscape-style';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ImageAspectRatioIcon from '@mui/icons-material/ImageAspectRatio';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import { Dispatch, SetStateAction } from 'react';
import IRunDetails from 'types/IRunDetails';
import { ITaskProgressEntry } from 'types/IWorkflowProgress';
import { detectFanOutNodes, buildCollapsedGraph, computeRegionSummary, PAGE_SIZE } from './collapseGraph';

cytoscape.use(cytoscapeDagre);

export default function TaskGraph({
  runDetails,
  setSelectedTaskIds,
  progressByTaskId,
}: {
  runDetails: IRunDetails;
  setSelectedTaskIds: Dispatch<SetStateAction<string[]>>;
  progressByTaskId?: Record<string, ITaskProgressEntry>;
}) {
  const resetPanZoomRef = useRef(() => {});
  const tooltipRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);
  // Ref rather than prop-capture so the mouseover handler always reads the
  // latest progress without needing to rebuild the cytoscape instance on every
  // poll (which would reset zoom/pan/selection).
  const progressRef = useRef<Record<string, ITaskProgressEntry> | undefined>(progressByTaskId);
  progressRef.current = progressByTaskId;

  const allFanOuts = useMemo(() => detectFanOutNodes(runDetails), [runDetails]);

  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(() => new Set(allFanOuts));

  const [fanOutOffsets, setFanOutOffsets] = useState<Map<string, number>>(() => new Map());

  const hasFanOuts = allFanOuts.size > 0;

  const elements = useMemo(
    () => (hasFanOuts ? buildCollapsedGraph(runDetails, collapsedNodes, fanOutOffsets) : getGraphData(runDetails)),
    [runDetails, collapsedNodes, fanOutOffsets, hasFanOuts],
  );

  const allCollapsed = hasFanOuts && collapsedNodes.size === allFanOuts.size;

  const onRef = useCallback(
    (div: HTMLDivElement | null) => {
      // React re-invokes this callback (old with null, then new with the same
      // div) whenever its deps change — e.g., when `elements` is rebuilt by
      // expand/collapse or pagination. Tear down the previous cy so we don't
      // leak its event listeners and internal state across rebuilds.
      cyRef.current?.destroy();
      cyRef.current = null;

      if (!div) {
        return;
      }

      const cy = cytoscape({
        container: div,
        elements,
        style: style as unknown as cytoscape.StylesheetJson,
      });
      cyRef.current = cy;

      cy.layout({ name: 'dagre' }).run();

      // Reposition pagination nodes to the edges of their sibling row
      cy.nodes('[?isPagination]').forEach((pagNode: cytoscape.NodeSingular) => {
        const parentId = pagNode.data('fanOutParentId');
        const direction = pagNode.data('paginationDirection');
        // Siblings: nodes connected to the same parent, excluding pagination nodes
        const siblings = cy
          .getElementById(parentId)
          .outgoers('node')
          .filter((n: cytoscape.NodeSingular) => !n.data('isPagination'));
        if (siblings.length === 0) return;
        const bb = siblings.boundingBox();
        const y = pagNode.position('y');
        const nodeWidth = pagNode.outerWidth();
        const gap = 20;
        if (direction === 'prev') {
          pagNode.position({ x: bb.x1 - nodeWidth / 2 - gap, y });
        } else {
          pagNode.position({ x: bb.x2 + nodeWidth / 2 + gap, y });
        }
      });

      /*
       * Selection: Like example, but use 'select' and 'unselect' instead of 'click':
       * https://js.cytoscape.org/#cy.collection
       * (Also, don't bother using cy.collection(), just array of id's.)
       */
      cy.nodes().on('mouseover', (event) => {
        div.style.cursor = 'pointer';
        const node = event.target;
        const breakdown = node.data('breakdown');
        const id = node.id();
        const isSummary = node.data('isSummary');
        let tipText: string | null = null;
        if (breakdown) {
          if (isSummary) {
            tipText = `${breakdown}\nClick to expand`;
          } else if (node.data('isPagination')) {
            const dir = node.data('paginationDirection');
            const step = node.data('pageStep');
            const dirLabel = dir === 'next' ? 'next' : 'previous';
            tipText = `${breakdown}\nClick to show ${dirLabel} ${step} branches`;
          } else {
            tipText = breakdown;
          }
        } else if (id && !isSummary && !node.data('isPagination')) {
          const taskType = node.data('taskType');
          const taskState = node.data('taskState');
          const workerLost = node.data('workerLost');
          const entry = progressRef.current?.[id];
          const lines = [`Task ID: ${id}`];
          if (taskType) lines.push(`Type: ${taskType}`);
          if (taskState) lines.push(`State: ${taskState}`);
          if (entry?.labels && Object.keys(entry.labels).length > 0) {
            lines.push('Labels:');
            for (const [k, v] of Object.entries(entry.labels)) {
              lines.push(`  ${k}: ${v}`);
            }
          }
          if (entry?.progress && Object.keys(entry.progress).length > 0) {
            lines.push('Progress:');
            for (const [k, v] of Object.entries(entry.progress)) {
              lines.push(`  ${k}: ${formatProgressValue(v)}`);
            }
          }
          if (workerLost) lines.push('⚠ Worker lost');
          tipText = lines.join('\n');
        }
        if (tipText && tooltipRef.current) {
          const pos = node.renderedPosition();
          const height = node.renderedOuterHeight();
          const tip = tooltipRef.current;
          tip.textContent = tipText;
          tip.style.display = 'block';
          tip.style.left = `${pos.x}px`;
          tip.style.top = `${pos.y + height / 2 + 8}px`;
        }
      });
      cy.nodes().on('mouseout', () => {
        div.style.cursor = 'default';
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }
      });

      cy.nodes().on('select', (event) => {
        const node = event.target;
        const id = node.id();

        // Click on summary node → expand
        if (node.data('isSummary')) {
          const parentId = node.data('fanOutParentId');
          node.unselect();
          if (tooltipRef.current) tooltipRef.current.style.display = 'none';
          setCollapsedNodes((prev) => {
            const next = new Set(prev);
            next.delete(parentId);
            return next;
          });
          return;
        }

        // Click on pagination node → page prev/next
        if (node.data('isPagination')) {
          const parentId = node.data('fanOutParentId');
          const direction = node.data('paginationDirection');
          node.unselect();
          if (tooltipRef.current) tooltipRef.current.style.display = 'none';
          setFanOutOffsets((prev) => {
            const next = new Map(prev);
            const current = prev.get(parentId) || 0;
            if (direction === 'next') {
              next.set(parentId, current + PAGE_SIZE);
            } else {
              next.set(parentId, Math.max(0, current - PAGE_SIZE));
            }
            return next;
          });
          return;
        }

        setSelectedTaskIds((ids) => ids.concat(id));
      });

      cy.nodes().on('unselect', (event) => {
        const node = event.target;
        if (node.data('isSummary') || node.data('isPagination')) return;
        const id = node.id();
        setSelectedTaskIds((ids) => ids.filter((remaining: string) => remaining !== id));
      });

      resetPanZoomRef.current = () => {
        cy.animate({ fit: { eles: cy.elements(), padding: 30 } }, { duration: 0 });
      };
    },
    [elements, setSelectedTaskIds],
  );

  // Live-update each node from the polled progress map so RUNNING→COMPLETED
  // (etc.) transitions show without a page reload. Runs on every new progress
  // snapshot and after onRef rebuilds cy (elements change). No relayout —
  // zoom/pan/selection preserved.
  //
  // For task nodes: update stateClass + taskState.
  // For summary/pagination nodes (collapsed fan-outs, paged sibling rows):
  // recompute pie breakdown from each node's stashed regionIds against the
  // latest task states, so collapsed-region views don't go stale.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !progressByTaskId) return;
    const effectiveStates: Record<string, string> = { ...runDetails.task_states };
    for (const [id, entry] of Object.entries(progressByTaskId)) {
      if (entry?.state) effectiveStates[id] = entry.state;
    }
    const workerLostSet = new Set(runDetails.worker_lost_task_ids || []);
    cy.batch(() => {
      cy.nodes().forEach((n) => {
        const isSummary = n.data('isSummary');
        const isPagination = n.data('isPagination');
        if (isSummary || isPagination) {
          const regionIds: string[] | undefined = n.data('regionIds');
          if (!regionIds) return;
          const { pieData, breakdown } = computeRegionSummary(regionIds, effectiveStates, workerLostSet);
          for (const [k, v] of Object.entries(pieData)) {
            if (n.data(k) !== v) n.data(k, v);
          }
          if (n.data('breakdown') !== breakdown) n.data('breakdown', breakdown);
          return;
        }
        const entry = progressByTaskId[n.id()];
        if (!entry?.state) return;
        const newClass = entry.state.toLowerCase();
        const oldClass = n.data('stateClass');
        if (oldClass === newClass) return;
        if (oldClass) n.removeClass(oldClass);
        n.addClass(newClass);
        n.data('stateClass', newClass);
        // Keep the tooltip's "State:" line in sync too.
        n.data('taskState', entry.state);
      });
    });
  }, [progressByTaskId, elements, runDetails.task_states, runDetails.worker_lost_task_ids]);

  const toggleAll = useCallback(() => {
    if (allCollapsed) {
      setCollapsedNodes(new Set());
    } else {
      setCollapsedNodes(new Set(allFanOuts));
    }
  }, [allCollapsed, allFanOuts]);

  return (
    <div
      css={css`
        flex-grow: 1;
        display: flex;
        flex-direction: column;

        position: relative;
        .graph-toolbar {
          position: absolute;
          top: 12px;
          left: 12px;
          z-index: 1;
          display: flex;
          gap: 4px;
        }
      `}
    >
      <div className="graph-toolbar">
        <Tooltip title="Reset pan/zoom">
          <IconButton onClick={() => resetPanZoomRef.current()} size="small">
            <ImageAspectRatioIcon />
          </IconButton>
        </Tooltip>
        {hasFanOuts && (
          <Tooltip title={allCollapsed ? 'Expand all fan-outs' : 'Collapse all fan-outs'}>
            <IconButton onClick={toggleAll} size="small">
              {allCollapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
            </IconButton>
          </Tooltip>
        )}
      </div>
      <div
        css={css`
          flex-grow: 1;
          min-height: 448px;
          position: relative;
        `}
      >
        <div
          ref={onRef}
          css={css`
            position: absolute;
            inset: 0;
          `}
        />
        <div
          ref={tooltipRef}
          css={css`
            display: none;
            position: absolute;
            transform: translateX(-50%);
            background: #333;
            color: white;
            font-size: 12px;
            font-family: monospace;
            padding: 4px 8px;
            border-radius: 4px;
            white-space: pre-line;
            text-align: center;
            pointer-events: none;
            z-index: 2;
          `}
        />
      </div>
    </div>
  );
}

function formatProgressValue(v: unknown): string {
  if (v === null) return 'null';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function getGraphData(runDetails: IRunDetails) {
  const workerLostSet = new Set(runDetails.worker_lost_task_ids || []);
  const nodes = runDetails.tasks.map((task) => {
    const stateClass = (runDetails.task_states[task.id] || '').toLowerCase();
    const classes = [stateClass];
    if (workerLostSet.has(task.id)) {
      classes.push('worker-lost');
    }
    return {
      data: {
        id: task.id,
        name: task.name,
        taskType: task.type,
        taskState: runDetails.task_states[task.id] || '',
        stateClass,
        workerLost: workerLostSet.has(task.id),
        width: Math.max(5, 8.5 * String(task.name).length + 20),
      },
      classes,
    };
  });

  const edges = Object.entries(runDetails.task_links).flatMap(([source, targets]) =>
    targets.map((target) => ({
      data: {
        source,
        target,
      },
    })),
  );

  return [...nodes, ...edges];
}
